import { toPercentage } from "@common/utilities";
import { TranslationStringType } from "@localization/LocalizationContext";
import { RiskOperationalSection } from "../riskOperationalOverview/RiskOperationalOverview.types";

// Which section of the Operational tab a tile's deep link should land on. A tile's figure can span both levels, but
// the tab shows one section at a time, so the link goes where the rows actually are: the organisation section only
// when the area assesses the organisation and the figure's entity half is empty. That makes an organisation-only
// area always land on its scenarios, and a Both area land on whichever half the figure came from — falling back to
// the entity section when both halves contribute, since that is the one the tab opens on anyway.
export function resolveOperationalSection(entityShare: number, assessesOrganisation: boolean): RiskOperationalSection {
    return assessesOrganisation && entityShare === 0 ? RiskOperationalSection.Organisation : RiskOperationalSection.Entities;
}

// What the Assessed tile's denominator counts. The unit comes from the area's assessment level rather than from the
// numbers themselves, so an area with nothing scored yet still names what it is going to count instead of falling
// back on "entity". An area assessed at one level names that level's unit; an area assessed at both breaks the
// number down per unit instead of merging it into a neutral "risks", so the reader can see which half the subjects
// sit in. Composed from two per-unit phrases rather than written as one sentence with two numbers in it, so each
// half keeps its own plural form: "of 1 entity, 1 scenario", not "1 scenarios".
//
// Empty while the areas menu is still resolving and neither level is known yet, so the tile never names a unit the
// area does not assess.
export function buildAssessedTotalLabel(
    totalEntities: number,
    totalOrganisationScenarios: number,
    assessesEntities: boolean,
    assessesOrganisation: boolean,
    translateString: TranslationStringType
): string {
    if (assessesEntities && assessesOrganisation) {
        const entities = translateString("ofEntitiesCount", { count: totalEntities });
        const scenarios = translateString("scenariosCount", { count: totalOrganisationScenarios });

        return `${entities}, ${scenarios}`;
    }

    if (assessesOrganisation) {
        return translateString("ofScenariosCount", { count: totalOrganisationScenarios });
    }

    if (assessesEntities) {
        return translateString("ofEntitiesCount", { count: totalEntities });
    }

    return "";
}

// A subject plotted on the matrix: an assessed entity at its worst cell, or an organisation scenario at its own.
// Structural on purpose, so the matrix can count entities and organisation scenarios from one list without a
// mapping layer in between.
export interface PlottedSubject {
    consequence: number | null;
    probability: number | null;
    currentConsequence: number | null;
    currentProbability: number | null;
}

// Counts plotted subjects into a scale×scale grid by the cell each occupies. cellOf picks which (consequence,
// probability) pair to bucket by: the subject's own cell for the live matrix, or its pre-mitigation cell for the
// movement comparison. Null or out-of-range cells are skipped.
export function buildCellCounts(
    subjects: Array<PlottedSubject>,
    scale: number,
    cellOf: (subject: PlottedSubject) => [number | null, number | null]
): Array<Array<number>> {
    const counts = Array.from({ length: scale }, () => Array.from({ length: scale }, () => 0));
    subjects.forEach((subject) => {
        const [consequence, probability] = cellOf(subject);
        if (consequence !== null && probability !== null && consequence >= 1 && consequence <= scale && probability >= 1 && probability <= scale) {
            counts[consequence - 1][probability - 1] += 1;
        }
    });
    return counts;
}

// Per cell, how the after-mitigation count compares with the current count: positive means the cell gained that
// many subjects (up arrow), negative means it lost that many (down arrow), zero is unchanged.
export function buildCellMovements(afterCounts: Array<Array<number>>, currentCounts: Array<Array<number>>): Array<Array<number>> {
    return afterCounts.map((column, consequenceIndex) =>
        column.map((afterCount, probabilityIndex) => afterCount - currentCounts[consequenceIndex][probabilityIndex])
    );
}

export interface ControlCoverage {
    // Entities with at least one risk over the area's appetite: the population where missing controls have to be
    // explained. Equals the over-appetite tile by construction, so the two never disagree.
    total: number;
    // Fully covered: every over-appetite risk on the entity has a linked control, so an uncovered second scenario
    // can never hide behind a covered first one.
    fullyCovered: number;
    // The remainder, so the two bands always add up to the total. A reader who subtracts and finds entities
    // unaccounted for stops trusting the tile.
    notFullyCovered: number;
    // Null when nothing is over appetite: 0% of nothing reads as total failure, so the tile renders a dash
    // (with its own "no risks over appetite" sub-line, since an empty denominator is good news here).
    percentage: number | null;
}

// Floored, never rounded: 99.6% coverage must not present itself as a clean 100%, which is the one number a
// reader takes as "nothing left to do".
export function getControlCoverage(total: number, fullyCovered: number): ControlCoverage {
    return {
        total,
        fullyCovered,
        notFullyCovered: Math.max(0, total - fullyCovered),
        percentage: total > 0 ? Math.floor(toPercentage(fullyCovered, total)) : null,
    };
}
